/*
 * GearFrost: linked options (dependent variant dropdowns).
 *
 * Replicates the old Debutify "Linked options": once an earlier option (e.g. Style) is
 * chosen, the later dropdowns (e.g. Size) show only the values that actually form a real
 * variant with it -- instead of Dawn's default "show every value + ' - Unavailable'".
 *
 * Two parts:
 *  1) Display filter -- hide the values a dependent dropdown can't make with the earlier
 *     selections. Runs on initial load and (via Dawn's preProcess pipeline) on every server
 *     re-render before it is swapped in, so there is no flicker.
 *  2) Selection repair -- when the shopper changes an earlier option, a lower dropdown may
 *     still point at a value the new selection doesn't come in (e.g. switch Style Men->Women
 *     while Size US5 is selected, but Women has no US5). Dawn only syncs the `selected`
 *     attribute on the dropdown the shopper touched, so it would fetch that stale combo as
 *     "- Unavailable". We listen in the CAPTURE phase (before Dawn's bubble-phase change
 *     handler reads `selectedOptionValues`) and re-point the lower dropdown to the first value
 *     that forms a real variant -- so Dawn fetches a valid, in-stock combo straight away. The
 *     re-pointed option still carries the " - Unavailable" label it was rendered with under the
 *     old selection, so we strip it for the brief window until the server swap restores it.
 *
 * Dropdown pickers only, and only when EVERY option is a dropdown (mirrors Debutify's
 * product_selector=="select" gate): if a swatch/button option is mixed in, the number of
 * <select>s no longer equals the option count, so we bail rather than mis-align positions.
 * Non-destructive -- invalid options are hidden via the `hidden` attribute (never removed,
 * so the live options collection never shrinks mid-loop and every real value stays
 * selectable). Valid combinations + the option count come from the slim JSON that
 * gf-linked-options.liquid prints as #gf-lo-data-{sectionId}.
 */
(function () {
  // Read from the variant-selects' own document first (so a server re-render is filtered
  // against the data that ships with it -- correct even for a combined-listing product swap),
  // then fall back to the live document.
  function readData(doc, sectionId) {
    var el =
      (doc && doc.getElementById && doc.getElementById('gf-lo-data-' + sectionId)) ||
      document.getElementById('gf-lo-data-' + sectionId);
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent);
      return data && data.variants && data.variants.length ? data : null;
    } catch (e) {
      return null;
    }
  }

  // Does a real variant start with these option values? (partial prefix match; trailing
  // option positions a shorter combo doesn't cover are ignored.)
  function comboExists(variants, vals) {
    return variants.some(function (v) {
      for (var i = 0; i < vals.length; i++) {
        if (v[i] !== vals[i]) return false;
      }
      return true;
    });
  }

  // The option dropdowns, in option order. Returns null if this isn't an all-dropdown picker
  // (a mixed swatch/dropdown product -- selects.length != optionCount -- is left untouched).
  function getDropdowns(variantSelects, data) {
    var selects = Array.prototype.slice.call(variantSelects.querySelectorAll('select.select__select'));
    var optionCount = data.optionCount || selects.length;
    if (selects.length < 2 || selects.length !== optionCount) return null;
    return selects;
  }

  // Display filter: hide each dependent value that can't form a real variant with the values
  // selected before it. The selected value is always kept visible.
  function filterVariantSelects(variantSelects, sectionId) {
    if (!variantSelects) return;
    var data = readData(variantSelects.ownerDocument, sectionId);
    if (!data) return;
    var selects = getDropdowns(variantSelects, data);
    if (!selects) return;
    var variants = data.variants;

    // currently selected value per option position (the server keeps option[selected] in sync)
    var selected = selects.map(function (sel) {
      var opt = sel.querySelector('option[selected]');
      return opt ? opt.value : null;
    });

    selects.forEach(function (sel, idx) {
      if (idx === 0) return; // the first option is the root -- always show all of its values
      var prior = selected.slice(0, idx);
      if (prior.indexOf(null) !== -1) return; // unknown prior selection -> leave this dropdown alone

      Array.prototype.forEach.call(sel.options, function (opt) {
        var exists = comboExists(variants, prior.concat([opt.value]));
        // never hide the option that is actually selected (kept valid by the repair pass below)
        opt.hidden = !exists && !opt.hasAttribute('selected');
      });
    });
  }

  // Selection repair: runs during the change event, before Dawn reads the new selection.
  // For each dependent dropdown whose current value no longer forms a real variant with the
  // values chosen ahead of it, move it to the first value that does. Reads `.value` (already
  // the freshly committed selection for every dropdown at change time -- the `selected`
  // attribute may still be stale), and sets BOTH the `selected` attribute (so Dawn's
  // `option[selected]` lookup follows) and `.value` (so the field shows the new value).
  function correctSelections(variantSelects, sectionId) {
    if (!variantSelects) return;
    var data = readData(variantSelects.ownerDocument, sectionId);
    if (!data) return;
    var selects = getDropdowns(variantSelects, data);
    if (!selects) return;
    var variants = data.variants;

    var chosen = [selects[0].value];
    for (var j = 1; j < selects.length; j++) {
      var sel = selects[j];
      if (comboExists(variants, chosen.concat([sel.value]))) {
        chosen.push(sel.value); // still valid with the (possibly just-changed) prefix
        continue;
      }
      // find the first value that forms a real variant with the chosen prefix
      var replacement = null;
      for (var k = 0; k < sel.options.length; k++) {
        if (comboExists(variants, chosen.concat([sel.options[k].value]))) {
          replacement = sel.options[k];
          break;
        }
      }
      if (!replacement) {
        chosen.push(sel.value); // no valid value (shouldn't happen for a real product) -> leave
        continue;
      }
      var prev = sel.querySelector('option[selected]');
      if (prev) prev.removeAttribute('selected');
      replacement.setAttribute('selected', 'selected');
      replacement.hidden = false; // the new selection must be visible even if it was filtered out
      // Drop the stale " - Unavailable" suffix this option was rendered with under the previous
      // selection, so the closed <select> doesn't flash it during the async re-fetch. The option
      // value is exactly its available-state label; the server swap restores authoritative text.
      if (replacement.textContent.trim() !== replacement.value) {
        replacement.textContent = replacement.value;
      }
      sel.value = replacement.value;
      chosen.push(replacement.value);
    }
  }

  function variantSelectsIn(node) {
    if (!node) return null;
    if (node.matches && node.matches('variant-selects')) return node;
    return node.querySelector ? node.querySelector('variant-selects') : null;
  }

  function init(productInfo) {
    if (!productInfo || productInfo.dataset.gfLoBound) return;
    var sectionId = productInfo.dataset.section;
    if (!sectionId) return;
    productInfo.dataset.gfLoBound = '1';

    // initial pass on what is already in the DOM
    filterVariantSelects(productInfo.querySelector('variant-selects'), sectionId);

    // repair a stale dependent selection in the capture phase -- before Dawn's bubble-phase
    // change handler reads selectedOptionValues and fetches the combo
    productInfo.addEventListener(
      'change',
      function (e) {
        var sel = e.target;
        if (!sel || !sel.matches || !sel.matches('select.select__select')) return;
        var vs = sel.closest && sel.closest('variant-selects');
        if (vs) correctSelections(vs, sectionId);
      },
      true
    );

    // and filter every server re-render, before it is swapped in (Dawn's own preprocess pipeline)
    if (typeof productInfo.addPreProcessCallback === 'function') {
      productInfo.addPreProcessCallback(function (html) {
        filterVariantSelects(variantSelectsIn(html), sectionId);
      });
    }
  }

  function boot() {
    document.querySelectorAll('product-info').forEach(init);
    document.addEventListener('product-info:loaded', function (e) {
      var pi = (e.target && e.target.closest && e.target.closest('product-info')) || e.target;
      init(pi);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
